import { t, useLanguage } from "@/lib/i18n";
import { spouseIds } from "@/lib/spouses";
import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { fileUrl } from "@/lib/api";
import { displayMemberName } from "@/lib/memberName";
import { memberPhoto, cardPhotoStyle, cardPhotoImage } from "@/lib/memberPhoto";
import { useAuth } from "@/context/AuthContext";
import MemberAlbums from "@/components/MemberAlbums";
import {
  Pencil, Trash2, Share2, User, Mars, Venus,
  Linkedin, Facebook, Globe, MapPin, Calendar, ExternalLink,
} from "lucide-react";

function Row({ label, children, action }) {
  useLanguage();
  if (!children && !action) return null;
  return (
    <div className="py-2 border-b border-[#E6DEC3]/60 flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-mono uppercase tracking-wider text-amber-800/70">{t(label)}</p>
        <div className="text-sm text-stone-800 mt-0.5 break-words">{children}</div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export default function MemberDetailDrawer({ person, persons, onClose, onEdit, onDelete, onShare, onNavigateMember, onAddRelative, previewOnly = false, selectionActions }) {
  useLanguage();
  const { user } = useAuth();
  if (!person) return null;
  const ownsRecord = person.owner_id === user?.id || user?.role === "admin";
  const canViewPrivate = ownsRecord || person.can_edit === true;
  const isOwner = !previewOnly && ownsRecord;
  const canEdit = !previewOnly && canViewPrivate;

  const byId = {};
  persons.forEach((p) => (byId[p.id] = p));
  const childIds = (person.children_ids || []).filter((id) => byId[id]);
  const personName = displayMemberName(person);
  const lineageLink = (id, relation) => {
    const relative = byId[id];
    if (!relative) return "—";
    const relativeName = displayMemberName(relative);
    if (previewOnly) return relativeName;
    return <button type="button" onClick={() => onNavigateMember(relative)}
      className="text-amber-800 underline underline-offset-2 hover:text-amber-600 focus-visible:outline focus-visible:outline-2 rounded-sm"
      data-testid={`lineage-${relation}-link`} aria-label={t("Focus {0} in their tree", { 0: relativeName })}>
      {relativeName}
    </button>;
  };
  const links = person.social_links || {};
  const addButton = (relation, label, disabled = false) => canEdit && (
    <Button size="sm" variant="outline" disabled={disabled}
      title={disabled ? t("Maximum four spouses") : label}
      onClick={() => onAddRelative(person, relation)} data-testid={`detail-add-${relation}-button`}>{t(label)}</Button>
  );

  return (
    <Sheet open={!!person} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-white w-full sm:max-w-md overflow-y-auto overscroll-contain p-0 pb-[env(safe-area-inset-bottom)]" data-testid="member-detail-drawer">
        <div className="bg-[#2C1E16] text-amber-50 p-6 parchment-grain">
          <div className="flex items-start gap-4">
            <div className="member-photo-frame w-20 h-20 bg-[#F4EFEB] border-2 border-[#D4A359] shrink-0">
              <div className="member-photo-surface">
              {memberPhoto(person) ? (
                <img src={fileUrl(cardPhotoImage(memberPhoto(person)).path)} alt={personName} style={cardPhotoStyle(cardPhotoImage(memberPhoto(person)))} className="w-full h-full object-cover" />
              ) : (
                <User size={34} className="text-stone-400" />
              )}
              </div>
            </div>
            <div className="min-w-0">
              <SheetHeader className="space-y-0 text-left">
                <SheetTitle className="font-serif text-2xl text-amber-50 break-words pr-5 flex items-center gap-2 leading-tight">
                  {personName}
                  {person.gender === "Male" && <Mars size={16} className="text-blue-300" />}
                  {person.gender === "Female" && <Venus size={16} className="text-pink-300" />}
                </SheetTitle>
                <SheetDescription className="sr-only">{t("Family member profile details")}</SheetDescription>
              </SheetHeader>
              {person.gotra && <p className="text-sm text-[#D4A359] font-mono mt-1">{person.gotra}</p>}
              <div className="flex flex-wrap gap-2 mt-2">
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${person.is_alive ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40" : "bg-stone-500/20 text-stone-300 border-stone-400/40"}`}>
                  {person.is_alive ? t("Living") : t("Deceased")}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-5">
            {canEdit && <Button size="sm" onClick={() => onEdit(person)} className="gold-btn text-[#2C1E16] border-0 flex-1" data-testid="detail-edit-button"><Pencil size={14} className="mr-1" />{t(" Edit")}</Button>}
            {isOwner && <Button size="sm" variant="outline" onClick={() => onShare(person)} className="border-amber-100/30 text-amber-50 bg-transparent hover:bg-amber-50/10" data-testid="detail-share-button"><Share2 size={14} /></Button>}
            {isOwner && <Button size="sm" variant="outline" onClick={() => onDelete(person)} className="border-red-300/40 text-red-200 bg-transparent hover:bg-red-500/20" data-testid="detail-delete-button"><Trash2 size={14} /></Button>}
          </div>
        </div>

        <div className="p-6">
          {selectionActions}
          <Row label={t("Gender")}>{t(person.gender)}</Row>
          <Row label={t("Caste")}>{person.caste}</Row>
          <Row label={t("Gotra")}>{person.gotra}</Row>
          <Row label={t("Religion")}>{t(person.religion)}</Row>
          {canViewPrivate && <>
          <Row label={t("Email")}>{person.email}</Row>
          <Row label={t("Phone number")}>{person.phone_number}</Row>
          <Row label={t("Profession/Business")}>{person.profession}</Row>
          <Row label={t("Country")}>{person.country}</Row>
          <Row label={t("State/Province")}>{person.state}</Row>
          <Row label={t("City")}>{person.city}</Row>
          {person.address && <Row label={t("Address")}><span className="whitespace-pre-wrap break-words">{person.address}</span></Row>}
          <Row label={t("Birth date")}><span className="flex items-center gap-2"><Calendar size={13} /> {person.birth_date || "—"}</span></Row>
          <Row label={t("Birth place")}><span className="flex items-center gap-2"><MapPin size={13} /> {person.birth_place || "—"}</span></Row>
          {!person.is_alive && <Row label={t("Date of Passing")}>{person.date_of_passing || "—"}</Row>}
          </>}
          <Row label={t("Father")} action={!person.father_id && addButton("father", "Add Father")}>{lineageLink(person.father_id, "father")}</Row>
          <Row label={t("Mother")} action={!person.mother_id && addButton("mother", "Add Mother")}>{lineageLink(person.mother_id, "mother")}</Row>
          <Row label={t("Spouses")} action={addButton("spouse", "Add Spouse", spouseIds(person).length >= 4)}>{spouseIds(person).length ? spouseIds(person).map((id, index) => <React.Fragment key={id}>{index > 0 && ", "}{lineageLink(id, index === 0 ? "spouse" : `spouse-${id}`)}</React.Fragment>) : "\u2014"}</Row>
          <Row label={t("Children")} action={addButton("child", "Add Child")}>
            {childIds.length ? childIds.map((id, index) => (
              <React.Fragment key={id}>
                {index > 0 && ", "}
                {lineageLink(id, `child-${id}`)}
              </React.Fragment>
            )) : "—"}
          </Row>
          {canViewPrivate && <>
          {person.notes && <Row label={t("Notes")}><p className="whitespace-pre-wrap">{person.notes}</p></Row>}

          {(links.linkedin || links.facebook || links.website) && (
            <div className="py-3 flex gap-3">
              {links.linkedin && <a href={links.linkedin} target="_blank" rel="noreferrer" className="text-amber-700 hover:text-amber-900" data-testid="link-linkedin"><Linkedin size={18} /></a>}
              {links.facebook && <a href={links.facebook} target="_blank" rel="noreferrer" className="text-amber-700 hover:text-amber-900" data-testid="link-facebook"><Facebook size={18} /></a>}
              {links.website && <a href={links.website} target="_blank" rel="noreferrer" className="text-amber-700 hover:text-amber-900" data-testid="link-website"><Globe size={18} /></a>}
            </div>
          )}

          <MemberAlbums key={person.id} photos={person.photos || []} memberName={personName} memberId={person.id} />

          {person.videos?.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] font-mono uppercase tracking-wider text-amber-800/70 mb-2">{t("Videos")}</p>
              <div className="space-y-2">
                {person.videos.map((m) => (
                  <a key={m.id} href={fileUrl(m.path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-amber-700 hover:underline">
                    <ExternalLink size={14} /> {m.filename}
                  </a>
                ))}
              </div>
            </div>
          )}
          </>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
